import { PrinterProfile } from '../../types/printerProfile';
import {
  PortedTicketData as TicketData,
  PortedCartItem as CartItem,
  PortedTaxResult as TaxResult,
} from './types';
import { aggregateCustomizationOptions, groupItemsByCategory } from './grouping';
import { calculateTax, getTaxDisplayFlags } from './tax';
import { currentLocale } from './dateFormat';
import { tk, tkPayment } from './i18nShim';
import { catStart, catEnd } from './categoryMarker';

/**
 * Tickets DÉDIÉS PAR MARQUE — Epson (TM-T20 / TM-m30, ESC/POS) et Star (TSP100).
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * CORRECTIF « LES PRIX NE S'IMPRIMENT PAS » (TM-T20, production)
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * SYMPTÔME : le ticket sortait complet mais SANS AUCUN MONTANT (ni prix d'article,
 * ni sous-total, ni TVA, ni ligne de paiement).
 *
 * CAUSE RACINE : on mélangeait DEUX techniques de mise en page incompatibles —
 * un padding d'espaces calculé en JS sur `profile.config.width` (48), le tout placé
 * DANS une balise `[L]`. Or le pont d'impression Android (DantSu) découpe chaque
 * ligne sur les balises `[L]/[C]/[R]`, puis remet lui-même en page CHAQUE colonne à
 * SA propre largeur (`nbrCharactersPerLine`, fixée côté Android). Ces deux largeurs
 * sont totalement indépendantes : rien, côté JS, ne les synchronise. Dès que la
 * largeur réelle du pont est inférieure à 48, tout ce qui dépasse est perdu — et ce
 * qui dépasse, c'est justement la colonne de droite, donc LES MONTANTS.
 * Seul le TOTAL survivait, parce qu'il passait par une ligne `[C]` courte.
 *
 * PREUVE : les deux formateurs qui fonctionnent en production ne mélangent JAMAIS
 * les deux techniques.
 *   • `ticketFormatterLegacy.ts` (l. 52, 173, 177, 220, 222) : `[L]libellé[R]montant`
 *     sur UNE SEULE ligne, ZÉRO padding — c'est le natif qui aligne, donc la largeur
 *     est forcément la bonne, quelle qu'elle soit.
 *   • `ticketFormatterModern.ts` : AUCUNE balise, padding pur.
 *
 * CORRECTIF : on calque exactement Legacy. Toute ligne « libellé / montant » est
 * désormais `[L]…[R]…`, sans le moindre espace de remplissage. Corollaires :
 *   • la grille manuelle QTE/LIBELLÉ/P.U./MONTANT a disparu (3 balises = 3 colonnes
 *     de largeur égale côté natif, ce qui casserait l'alignement) : le prix unitaire
 *     est replié dans le segment de gauche quand la quantité est > 1 ;
 *   • plus aucune ligne ne dépend de `width` — le ticket est désormais correct que le
 *     pont soit configuré en 32, 42 ou 48 colonnes. `width` ne sert plus qu'aux
 *     séparateurs et au découpage des textes longs.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * AUTRES RÈGLES APPLIQUÉES (les pièges, et pourquoi on les évite)
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * • OCTETS ESC/POS SUR LEUR PROPRE LIGNE (motif de `ticketFormatterModern.ts`, qui
 *   fait `lines.push('\x1B\x21\x18')` en élément autonome). Un octet ESC COLLÉ devant
 *   une balise (`"\x1B\x21\x00[L]…"`) fabrique une colonne supplémentaire côté natif
 *   (le fragment non balisé), et les colonnes se partagent la largeur : la ligne se
 *   retrouvait décalée d'une demi-largeur. C'était le défaut secondaire du
 *   `pendingReset`, désormais supprimé.
 * • JAMAIS DE DOUBLE LARGEUR (`ESC ! 0x20`). Le natif calcule ses colonnes en NOMBRE
 *   DE CARACTÈRES : en double largeur un caractère occupe 2 colonnes, donc le
 *   centrage et le `[R]` tombent à côté. Modern et Legacy utilisent `\x1B\x21\x18`
 *   (double HAUTEUR + gras) pour le nom du magasin et le TOTAL : on fait pareil.
 * • PAS de `ESC @` (init) : il remet la page de code à l'usine (PC437, sans majuscules
 *   accentuées) et peut annuler la configuration posée par le natif.
 * • PAS de `ESC a` / `ESC GS a` (alignement) : conflit direct avec `[C]/[L]/[R]`.
 * • PAS de page de code (`ESC t`, `ESC R`) : `ESC R 1` (France) substitue 14 positions
 *   ASCII dont `[`, `]` et `|` → il DÉTRUIRAIT les balises du pont.
 * • PAS de commande de coupe ni de lignes d'avance : le natif coupe, et
 *   `printQueue.stripMarkers()` (`\n{3,} → \n\n` puis `.trim()`) les détruirait.
 * • CÔTÉ STAR : ZÉRO OCTET BRUT. Une TSP100 sort d'usine en Star Graphic Mode et ne
 *   comprend pas l'ESC/POS ; c'est le chemin natif `StarIOPort` qui rasterise à
 *   partir des seules balises.
 */

export type PrinterBrand = 'epson' | 'star';

/**
 * Style d'une marque. Il n'y a PAS de « CommandSet » complet par marque :
 * l'alignement et le gras passent par les balises du pont pour les DEUX marques.
 * Seul Epson reçoit encore des octets bruts, et uniquement pour la TAILLE —
 * exactement les séquences que Modern et Legacy émettent déjà en production.
 */
interface BrandStyle {
  /** Mise en valeur maximale : double hauteur + gras (nom du magasin, TOTAL). */
  sizeBig: string;
  /** Double hauteur seule. */
  sizeTall: string;
  /** Retour taille normale. */
  sizeNormal: string;
}

const EPSON: BrandStyle = {
  // ⚠️ 0x18 = 0x10 (double hauteur) | 0x08 (gras). SURTOUT PAS 0x30, qui ajoute la
  // double LARGEUR : le natif compterait 1 caractère là où l'imprimante en occupe 2,
  // et tout l'alignement (centrage, colonne [R]) partirait à droite.
  sizeBig: '\x1B\x21\x18',
  sizeTall: '\x1B\x21\x10', // ESC ! 0x10 — double hauteur seule
  sizeNormal: '\x1B\x21\x00', // ESC ! 0x00
};

/** Star : AUCUN octet brut. Le natif rasterise à partir des balises. */
const STAR: BrandStyle = { sizeBig: '', sizeTall: '', sizeNormal: '' };

/* ────────────────────────────── Helpers de mise en page ───────────────────── */

const round2 = (n: unknown): number => Math.round(((Number(n) || 0) + Number.EPSILON) * 100) / 100;

/**
 * Montant à la française : « 1234,56 ». Aucun symbole sur les lignes courantes —
 * le « EUR » n'apparaît que sur la ligne TOTAL (pas de pari sur le glyphe « € »,
 * dont le rendu dépend de la page de code de l'imprimante).
 */
const money = (n: unknown): string => (Number(n) || 0).toFixed(2).replace('.', ',');

/** Découpe un texte en lignes de `width` caractères max, sans couper les mots. */
function wrap(text: unknown, width: number): string[] {
  const clean = String(text ?? '').replace(/\s+/g, ' ').trim();
  if (!clean) return [];
  if (clean.length <= width) return [clean];

  const out: string[] = [];
  let line = '';
  for (const word of clean.split(' ')) {
    if (!line.length) {
      line = word;
    } else if (line.length + 1 + word.length <= width) {
      line += ' ' + word;
    } else {
      out.push(line);
      line = word;
    }
    // Mot plus long que la ligne (URL, référence) : on le coupe de force.
    while (line.length > width) {
      out.push(line.slice(0, width));
      line = line.slice(width);
    }
  }
  if (line) out.push(line);
  return out;
}

/* ─────────────────────────────── Construction ─────────────────────────────── */

export interface BrandTicketOptions {
  /** Regrouper les options identiques d'un article (profil : `aggregateOptions`). */
  aggregateOptions?: boolean;
  /** Imprimer les notes d'article (profil : `formatNotes`). */
  formatNotes?: boolean;
  /**
   * Mode d'affichage de la TVA issu du PROFIL (`config.taxDisplay.displayMode`).
   * ⚠️ Tous les autres formateurs (Modern, Legacy, Advanced, Custom, Ultra,
   * Professional) lisent le profil ; ce module lisait `data.taxSettings` et ignorait
   * donc silencieusement le réglage TVA du profil Epson/Star.
   */
  taxDisplayMode?: string;
}

/**
 * Construit le ticket pour une marque donnée.
 * `width` ne sert plus qu'aux séparateurs et au découpage des textes longs :
 * l'alignement des montants est délégué au pont (`[L]…[R]…`), donc un écart entre
 * `width` et la largeur réelle du pont n'ampute plus les prix.
 */
export function buildBrandTicket(
  data: TicketData,
  brand: PrinterBrand,
  width = 48,
  options?: BrandTicketOptions
): string {
  const W = Math.max(24, Math.floor(Number(width) || 48));
  const S = brand === 'star' ? STAR : EPSON;
  const aggregateOpts = options?.aggregateOptions !== false;
  const formatNotes = options?.formatNotes !== false;

  const out: string[] = [];

  /** Balise STRUCTURELLE : consommée par le JS (printQueue), jamais vue par l'imprimante. */
  const marker = (m: string): void => {
    out.push(m);
  };

  /**
   * Octet ESC/POS BRUT, poussé SEUL sur sa ligne (motif Modern, prouvé en production).
   * Le coller devant une balise créerait une colonne parasite côté natif.
   */
  const raw = (cmd: string): void => {
    if (cmd) out.push(cmd);
  };

  /**
   * ⚠️ DIVERGENCE ASSUMÉE AVEC LA CONSOLE : elle sépare au TIRET, on sépare au `=`.
   *
   * Dès que le tampon du pré-traitement natif contient 24 tirets consécutifs ou plus,
   * deux lignes IDENTIQUES consécutives font DISPARAÎTRE la seconde. Sur une borne,
   * deux articles identiques d'affilée sont le cas NORMAL (deux menus enfant, deux
   * cafés) : l'article s'évaporerait du ticket cuisine sans aucune erreur. Le `=`
   * produit exactement la même barre et n'arme rien.
   */
  const separator = '='.repeat(W);
  const rule = (): void => out.push(`[C]${separator}`);

  /** Bloc centré : balise `[C]` SEULE, jamais de padding manuel (double centrage). */
  const centered = (text: unknown, opts?: { size?: 'big' | 'tall'; bold?: boolean }): void => {
    const clean = String(text ?? '').replace(/\s+/g, ' ').trim();
    if (!clean) return;

    const size = opts?.size === 'big' ? S.sizeBig : opts?.size === 'tall' ? S.sizeTall : '';
    if (size) raw(size);
    for (const line of wrap(clean, W)) {
      out.push('[C]' + (opts?.bold ? `<b>${line}</b>` : line));
    }
    if (size) raw(S.sizeNormal);
  };

  /** Bloc aligné à gauche. Une seule colonne → le découpage manuel est sans danger. */
  const leftLine = (text: unknown, bold = false): void => {
    for (const line of wrap(text, W)) {
      out.push('[L]' + (bold ? `<b>${line}</b>` : line));
    }
  };

  /**
   * Ligne à DEUX COLONNES NATIVES : `[L]gauche[R]droite`, exactement comme
   * `ticketFormatterLegacy.ts`. AUCUN espace de remplissage : c'est le pont qui
   * aligne, à SA largeur. C'est le correctif central du bug « prix invisibles ».
   */
  const rowLine = (left: unknown, right: unknown, bold = false): void => {
    // ⚠️ On ne rogne QUE la fin du segment de gauche : les espaces de TÊTE sont
    // porteurs de sens (indentation des suppléments « ␣␣+ Extra fromage »).
    // Les retours à la ligne, eux, sont interdits : ils casseraient la ligne en deux
    // et la balise `[R]` se retrouverait orpheline.
    const l = String(left ?? '').replace(/[\r\n\t]+/g, ' ').replace(/\s+$/, '');
    const r = String(right ?? '').replace(/\s+/g, ' ').trim();
    if (!l.trim() && !r) return;
    const lTag = l ? (bold ? `[L]<b>${l}</b>` : `[L]${l}`) : '[L]';
    const rTag = r ? (bold ? `[R]<b>${r}</b>` : `[R]${r}`) : '';
    out.push(lTag + rTag);
  };

  /* ── Données ─────────────────────────────────────────────────────────────── */

  const items: CartItem[] = Array.isArray(data.items) ? data.items : [];
  const categories = Array.isArray(data.categories) ? data.categories : [];
  const totalArticles = items.reduce((sum, it) => sum + (Number(it?.quantity) || 0), 0);

  // Ticket cuisine « petit format » : pas de totaux, pas de paiement, pas de pied.
  const isKitchen = data.ticketType === 'category-small';

  /**
   * `data.orderDate` est une CHAÎNE ISO. Un `String(...)` imprimerait l'ISO brut
   * (« 2026-07-20T18:14:00.000Z »).
   */
  const orderDate = data.orderDate
    ? typeof data.orderDate === 'string'
      ? new Date(data.orderDate)
      : data.orderDate
    : new Date();
  const dateStr = Number.isNaN(orderDate.getTime())
    ? ''
    : orderDate.toLocaleString(currentLocale(), {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });

  // Numéro de commande : 2 derniers chiffres (grand n° rapide) sauf demande explicite
  // du code complet (commandes web livraison, pour rapprochement avec le site).
  const rawCode = String(data.orderCode ?? '');
  const shortCode =
    rawCode && rawCode !== 'N/A' ? rawCode.slice(-2).padStart(2, '0') : rawCode;
  const code = (data.printFullOrderCode ? rawCode : shortCode) || 'N/A';

  const typeLabel =
    data.orderType === 'delivery'
      ? tk('orderType.deliveryUpper')
      : data.orderType === 'takeaway'
      ? tk('orderType.takeawayUpper')
      : tk('orderType.dineInUpper');

  /* ── TVA : EXTRACTION du TTC, jamais d'ajout ──────────────────────────────
   * ⚠️ `data.taxResult` est une UNION `TaxResult | Array<{rate,amount}>` : sans le
   * `Array.isArray()`, `.totalTTC` serait `undefined` sur la variante tableau.
   * ⚠️ On ne re-somme JAMAIS les lignes pour obtenir le total : la somme des lignes
   * ≠ `invoiceTotal` (remise fidélité, livraison, arrondis) et un écart d'un centime
   * rend le ticket non conforme. Le TOTAL imprimé est celui du pipeline.
   * ⚠️ `orderTotals.subtotal` contient le HT (orderToTicketData.ts) → libellé
   * « Sous-total HT », jamais « Sous-total ». */
  const tr: TaxResult | undefined =
    data.taxResult && !Array.isArray(data.taxResult) ? (data.taxResult as TaxResult) : undefined;
  const totals = data.orderTotals;

  const totalTTC = round2(tr?.totalTTC ?? totals?.invoiceTotal ?? 0);
  const loyaltyDiscount = round2(totals?.loyaltyDiscount || 0);
  const loyaltyPointsUsed = Number(totals?.loyaltyPointsUsed) || 0;
  // HT et TVA se calculent AVANT la remise fidélité (cohérent avec les lignes
  // d'articles imprimées) ; le TOTAL reste le montant réellement payé.
  const baseTTC = loyaltyDiscount > 0 ? round2(totalTTC + loyaltyDiscount) : totalTTC;

  const apiRate = tr?.taxRate ?? null;
  const hasHT = tr?.subtotalHT !== undefined && tr?.subtotalHT !== null;
  const hasTax = tr?.taxAmount !== undefined && tr?.taxAmount !== null;
  // Repli : le helper PARTAGÉ du projet (aucune logique de TVA maison ici).
  const fb = hasHT && hasTax && apiRate !== null ? null : calculateTax(baseTTC, apiRate);
  const taxRate = apiRate !== null ? apiRate : fb ? fb.taxRate : 0;
  const subtotalHT = hasHT ? round2(tr?.subtotalHT) : round2(fb?.subtotalHT ?? totals?.subtotal ?? 0);
  const taxAmount = hasTax ? round2(tr?.taxAmount) : round2(fb?.taxAmount ?? totals?.taxAmount ?? 0);
  const rateFmt = taxRate % 1 === 0 ? taxRate.toFixed(0) : taxRate.toFixed(1);

  // Priorité au réglage du PROFIL (comme tous les autres formateurs), repli sur
  // `data.taxSettings`. `'hidden'` (vocabulaire ticket) ↔ `'compact'` (vocabulaire du
  // helper partagé).
  const rawMode = options?.taxDisplayMode || data.taxSettings?.displayMode;
  const taxFlags = getTaxDisplayFlags(rawMode === 'hidden' ? 'compact' : rawMode || 'detailed');

  /* ══ 1. En-tête établissement ══════════════════════════════════════════════ */
  marker('[HEADER:START]');
  const storeName = data.storeInfo?.name?.trim();
  centered(storeName ? storeName.toUpperCase() : tk('header.defaultRestaurant'), {
    size: 'big',
    bold: true,
  });
  if (data.storeInfo?.address) centered(data.storeInfo.address);
  if (data.storeInfo?.phone) centered(`${tk('header.phoneShort')}: ${data.storeInfo.phone}`);
  if (data.profileSettings?.siretMessage?.trim()) centered(data.profileSettings.siretMessage);
  if (data.profileSettings?.tvaMessage?.trim()) centered(data.profileSettings.tvaMessage);
  rule();

  /* ══ 2. En-tête transaction ════════════════════════════════════════════════ */
  rowLine(`${tk('header.receiptUpper')} N°${code}`, dateStr, true);

  const seller = [data.seller_code, data.created_by_name]
    .filter((v) => !!v && String(v).trim() !== '')
    .join(' - ');
  const tableTxt = data.tableNumber ? `${tk('header.table')} ${data.tableNumber}` : '';
  if (seller || tableTxt) rowLine(seller ? `${tk('header.seller')} : ${seller}` : '', tableTxt);

  if (data.customerName) leftLine(`${tk('header.client')} : ${data.customerName}`);
  if (data.confirmationCode !== undefined && data.confirmationCode !== null) {
    leftLine(`${tk('header.clientNumber')} : ${data.confirmationCode}`);
  }
  centered(typeLabel, { bold: true });
  marker('[HEADER:END]');

  /* ══ 3. En-tête des colonnes ═══════════════════════════════════════════════ */
  rule();
  // Deux colonnes natives, comme les lignes d'articles qu'elles annoncent.
  rowLine(tk('items.sectionTitle'), tk('items.amountUpper'), true);
  rule();

  /* ══ 4. Articles ═══════════════════════════════════════════════════════════ */
  marker('[ITEMS:START]');

  const pushItem = (item: CartItem): void => {
    const qty = Number(item?.quantity) || 1;
    const name = String(item?.menuItem?.name ?? '').trim() || tk('items.product');
    // P.U. TTC. Les suppléments ne sont PAS inclus dans le prix de ligne → chacun a
    // sa propre ligne chiffrée (traçabilité NF525 + lisibilité).
    const unit = Number(item?.menuItem?.price) || 0;

    // Le prix unitaire est REPLIÉ dans le segment de gauche : une 3ᵉ balise ferait
    // 3 colonnes de largeur égale côté natif et casserait l'alignement des montants.
    // On ne l'affiche que si la quantité > 1, seul cas où il apporte une information.
    const label = qty > 1 ? `${qty}x ${name} (${money(unit)})` : `${qty}x ${name}`;
    rowLine(label, money(round2(unit * qty)));

    // Options / suppléments choisis. `customizations` est déjà filtré sur
    // `is_user_selected` par orderToTicketData → aucun filtrage à refaire ici.
    const customizations = item?.customizations || {};
    for (const [, rawOptions] of Object.entries(customizations)) {
      const list = Array.isArray(rawOptions) ? rawOptions : [];
      const opts = aggregateOpts
        ? aggregateCustomizationOptions(list)
        : list.map((o) => ({ name: o.name, price: o.price, quantity: 1 }));

      for (const opt of opts) {
        const oQty = Number(opt.quantity) || 1;
        const oPrice = Number(opt.price) || 0;
        const optLabel = `  + ${String(opt.name ?? '').trim()}${oQty > 1 ? ` x${oQty}` : ''}`;
        rowLine(optLabel, oPrice > 0 ? `+${money(round2(oPrice * oQty))}` : '');
      }
    }

    const note = String(item?.note ?? '').trim();
    if (formatNotes && note) {
      wrap(note, W - 6).forEach((l, i) => out.push('[L]' + (i === 0 ? '  > ' : '    ') + l));
    }
  };

  if (items.length === 0) {
    centered(tk('items.none'));
  } else if (categories.length > 0) {
    // Tri par `category.sequence` (ordre du menu) assuré par groupItemsByCategory.
    const groups = groupItemsByCategory(items, categories);
    groups.forEach((group, i) => {
      const qtyTotal = group.items.reduce((s, it) => s + (Number(it?.quantity) || 0), 0);

      /* Le titre de catégorie est placé HORS des balises [CAT:…] : sur un ticket
       * cuisine, `printQueue.buildCategoryTicket()` réimprime déjà lui-même
       * « *** CATÉGORIE *** ». L'inclure donnerait un titre EN DOUBLE. */
      centered(`-- ${group.categoryName.toUpperCase()} (${qtyTotal}) --`, { bold: true });

      // Balises de ventilation cuisine — seul mécanisme de routage (helper partagé :
      // échappement garanti du nom, sinon un `]` casserait la regex de printQueue).
      marker(catStart(group.categoryId, group.categoryName, i));
      group.items.forEach(pushItem);
      marker(catEnd(group.categoryId, group.categoryName, i));

      // Séparateur ENTRE deux groupes seulement, jamais après le dernier.
      if (i < groups.length - 1) rule();
    });
  } else {
    // Repli : aucune catégorie connue (API catégories en échec) → liste à plat.
    items.forEach(pushItem);
  }

  marker('[ITEMS:END]');

  /* ══ 5. Totaux ═════════════════════════════════════════════════════════════ */
  if (!isKitchen) {
    rule();
    marker('[TOTALS:START]');
    leftLine(
      `${totalArticles} ${totalArticles > 1 ? tk('items.articles') : tk('items.article')}`
    );

    if (taxFlags.showSubtotalHT) rowLine(tk('totals.subtotalHT'), money(subtotalHT));
    if (taxFlags.showTaxAmount) {
      // ⚠️ Dette connue : `TaxResult` ne porte QU'UN taux → pas de ventilation
      // multi-taux (tableau TAUX/BASE/TVA/TTC) sans changer le pipeline en amont.
      rowLine(tk('totals.taxWithRate', { rate: rateFmt }), money(taxAmount));
    }
    if (loyaltyDiscount > 0) {
      rowLine(
        loyaltyPointsUsed > 0
          ? tk('totals.loyaltyDiscountPoints', { points: loyaltyPointsUsed })
          : tk('totals.loyaltyDiscount'),
        `-${money(loyaltyDiscount)}`
      );
    }

    // Remises NOMMÉES de la borne (HAPPY HOUR) — libellé et montant OFFICIELS du serveur.
    // ⚠️ NF525 : un prix différent du catalogue doit être justifié sur le ticket.
    for (const line of Array.isArray(data.extraDiscountLines) ? data.extraDiscountLines : []) {
      const amount = round2(line?.amount);
      if (amount > 0) rowLine(line.label, `-${money(amount)}`);
    }

    rule();
    // TOTAL en double HAUTEUR (jamais double largeur) + deux colonnes natives : le
    // montant reste aligné à droite quelle que soit la largeur réelle du pont.
    // « EUR » et non « € » : le glyphe euro dépend de la page de code de l'imprimante.
    raw(S.sizeBig);
    rowLine(tk('totals.totalTTCUpper'), `${money(totalTTC)} EUR`, true);
    raw(S.sizeNormal);
    rule();

    /* ══ 6. Paiement ═════════════════════════════════════════════════════════ */
    const payLines = Array.isArray(data.paymentLines) ? data.paymentLines : [];
    if (data.isPaymentPending) {
      centered(tk('payment.toCollect'), { bold: true });
    } else if (payLines.length > 1) {
      rowLine(`${tk('payment.label')} : ${tk('payment.mixed')}`, '', true);
      for (const p of payLines) rowLine(`  ${p.label}`, money(p.amount));
    } else if (payLines.length === 1) {
      rowLine(`${tk('payment.label')} : ${payLines[0].label}`, money(payLines[0].amount), true);
    } else {
      const label =
        data.paymentModeLabel ||
        (data.paymentMethod ? tkPayment(data.paymentMethod) : tk('payment.notSpecified'));
      rowLine(`${tk('payment.label')} : ${label}`, '', true);
    }
    marker('[TOTALS:END]');
  }

  /* ══ 7. Notes de commande ══════════════════════════════════════════════════ */
  if (data.delivery_note || data.pickup_note) {
    rule();
    centered(tk('items.notesUpper'), { bold: true });
    if (data.delivery_note) leftLine(`${tk('notes.delivery')}: ${data.delivery_note}`);
    if (data.pickup_note) leftLine(`${tk('notes.pickup')}: ${data.pickup_note}`);
  }

  /* ══ 8. Pied de ticket ═════════════════════════════════════════════════════ */
  if (!isKitchen) {
    rule();
    marker('[FOOTER:START]');
    centered(tk('footer.thankYouExcl'), { bold: true });
    marker('[FOOTER:END]');
  }

  // ⚠️ AUCUNE commande de coupe, AUCUNE ligne d'avance : le natif coupe, et
  // `stripMarkers()` (`\n{3,} → \n\n` puis `.trim()`) détruirait les lignes vides.

  return out.join('\n');
}

/** Epson TM-T20 / TM-m30 et compatibles ESC/POS. */
export const buildEpsonTicket = (data: TicketData, profile?: PrinterProfile): string =>
  buildBrandTicket(data, 'epson', Number(profile?.config?.width) || 48, {
    aggregateOptions: profile?.config?.aggregateOptions !== false,
    formatNotes: profile?.config?.formatNotes !== false,
    taxDisplayMode: profile?.config?.taxDisplay?.displayMode,
  });

/** Star TSP100 et compatibles — texte + balises uniquement, aucun octet brut. */
export const buildStarTicket = (data: TicketData, profile?: PrinterProfile): string =>
  buildBrandTicket(data, 'star', Number(profile?.config?.width) || 48, {
    aggregateOptions: profile?.config?.aggregateOptions !== false,
    formatNotes: profile?.config?.formatNotes !== false,
    taxDisplayMode: profile?.config?.taxDisplay?.displayMode,
  });
